/**
 * Affiliate Service - Handle referral tracking and rewards
 */
import { Connection, PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import { Program, BN } from '@project-serum/anchor';
import { 
  getConnection, 
  getProgram, 
  findPDA, 
  solToLamports, 
  lamportsToSol,
  handleError,
  PROGRAM_ID,
  AUCTION_SEED,
  AFFILIATE_SEED
} from './baseService';
import auctionService from './auctionService';

// Affiliate commission rate (5%)
const AFFILIATE_COMMISSION_RATE = 0.05;

class AffiliateService {
  constructor() {
    this.connection = null;
    this.program = null;
    this.wallet = null;
    this.initialized = false;
    this.auctionPDA = null;
  }

  /**
   * Initialize the affiliate service
   * @param {Connection} connection - Solana connection
   * @param {Object} wallet - Solana wallet
   * @param {PublicKey} auctionPDA - Auction PDA
   * @returns {boolean} - Whether initialization was successful
   */
  async initialize(connection, wallet, auctionPDA) {
    try {
      this.connection = connection;
      this.wallet = wallet;
      this.auctionPDA = auctionPDA;
      
      // Load the program
      this.program = await getProgram(connection, wallet);
      
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize affiliate service:', error);
      return false;
    }
  }

  /**
   * Generate a referral link for the current wallet
   * @returns {string} - Referral link
   */
  generateReferralLink() {
    if (!this.initialized || !this.wallet.publicKey) {
      throw new Error('Affiliate service not initialized or wallet not connected');
    }

    // Create a referral link with the wallet address as the referrer
    const baseUrl = window.location.origin;
    const referralLink = `${baseUrl}?ref=${this.wallet.publicKey.toString()}`;
    
    return referralLink;
  }

  /**
   * Get affiliate stats for the current wallet
   * @returns {Promise<Object>} - Affiliate stats
   */
  async getAffiliateStats() {
    if (!this.initialized) {
      throw new Error('Affiliate service not initialized');
    }

    try {
      // Find the affiliate stats PDA
      const [affiliatePDA] = await findPDA([
        Buffer.from(AFFILIATE_SEED),
        this.auctionPDA.toBuffer(),
        this.wallet.publicKey.toBuffer()
      ]);
      
      // Fetch affiliate stats
      const affiliateStats = await this.program.account.affiliateStats.fetch(affiliatePDA);
      
      // Get auction state to calculate earnings
      const auctionState = await auctionService.getAuctionState();
      
      // Calculate earnings based on the number of referrals and the affiliate pool
      const totalEarnings = affiliateStats.totalReferrals > 0
        ? (affiliateStats.totalReferrals / auctionState.totalParticipants) * auctionState.affiliatePool
        : 0;
      
      return {
        totalReferrals: affiliateStats.totalReferrals,
        activeReferrals: affiliateStats.activeReferrals,
        totalEarnings,
        claimedRewards: affiliateStats.claimedRewards,
        claimableRewards: totalEarnings - affiliateStats.claimedRewards
      };
    } catch (error) {
      // User might not be an affiliate yet
      return {
        totalReferrals: 0,
        activeReferrals: 0,
        totalEarnings: 0,
        claimedRewards: 0,
        claimableRewards: 0
      };
    }
  }

  /**
   * Check if a referrer is valid
   * @param {string} referrer - Referrer public key
   * @returns {Promise<boolean>} - Whether the referrer is valid
   */
  async isValidReferrer(referrer) {
    if (!this.initialized) {
      throw new Error('Affiliate service not initialized');
    }

    try {
      // Validate the public key format
      const referrerPubkey = new PublicKey(referrer);
      
      // Ensure referrer is not the same as the current wallet
      if (referrerPubkey.equals(this.wallet.publicKey)) {
        return false; // Cannot refer yourself
      }
      
      // Check if the referrer exists on-chain
      // This is a simplified check - in a real implementation, you might want to
      // check if the referrer has a valid affiliate stats account
      const accountInfo = await this.connection.getAccountInfo(referrerPubkey);
      return accountInfo !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the referrer from URL parameters
   * @returns {string|null} - Referrer public key or null if not found
   */
  getReferrerFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('ref');
    
    if (referrer) {
      try {
        // Validate the public key format
        new PublicKey(referrer);
        return referrer;
      } catch (error) {
        return null;
      }
    }
    
    return null;
  }

  /**
   * Calculate affiliate rewards
   * @param {number} contributionAmount - Contribution amount in SOL
   * @returns {number} - Affiliate reward in JINU
   */
  calculateAffiliateReward(contributionAmount) {
    // Calculate the commission based on the contribution amount
    const commission = contributionAmount * AFFILIATE_COMMISSION_RATE;
    
    // In a real implementation, you would convert this to JINU based on the
    // auction state and tokenomics. For simplicity, we'll use a fixed rate.
    const jinuReward = commission * 1000; // Example: 1 SOL = 1000 JINU
    
    return jinuReward;
  }

  /**
   * Claim affiliate rewards
   * @returns {Promise<string>} - Transaction signature
   */
  async claimAffiliateRewards() {
    if (!this.initialized) {
      throw new Error('Affiliate service not initialized');
    }

    try {
      // This functionality is already implemented in the contributionService.claimRewards()
      // method, as the claim_rewards instruction handles both user rewards and affiliate rewards.
      // We'll just forward the call to that method.
      const contributionService = await import('./contributionService').then(module => module.default);
      return await contributionService.claimRewards();
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Track a referral contribution
   * This method is called internally by the contributionService when a user
   * contributes with a referrer. It's not meant to be called directly.
   * @param {string} referrer - Referrer public key
   * @param {number} amount - Contribution amount in SOL
   * @returns {Promise<void>}
   */
  async trackReferralContribution(referrer, amount) {
    if (!this.initialized) {
      throw new Error('Affiliate service not initialized');
    }

    try {
      // This is handled automatically by the contribute instruction in the smart contract
      // when a referrer is provided. No need to make a separate call.
      console.log(`Tracked referral contribution: ${amount} SOL from ${this.wallet.publicKey.toString()} to ${referrer}`);
    } catch (error) {
      console.error('Failed to track referral contribution:', error);
    }
  }
}

// Create a singleton instance
const affiliateService = new AffiliateService();
export default affiliateService;
